/**
 * Product mapping functions for the Advanced Retirement Calculator
 * 
 * NOTE: These individual mapping functions are DEPRECATED.
 * The autofill.ts module now uses calculateBalances from 
 * ~/calculations/portfolioCalculations/calculateBalances.ts for accurate
 * currency conversion that matches the rest of the app.
 * 
 * The functions below are kept for backward compatibility but should not
 * be used directly. Use buildAutofillFromMoneytor() instead.
 * 
 * @module domain/retirement/productMappers
 * @deprecated Use buildAutofillFromMoneytor from autofill.ts instead
 */

import type { Fiat } from "~/types/global.types";
import type {
  BankAsset,
  CryptoAsset,
  DebtAsset,
  OtherAsset,
  PensionAsset,
  RealestateAsset,
  RSUAsset,
  ShareAsset,
} from "~/types/assets.types";
import { Forms } from "~/utils/products";

import {
  AssetCategory,
  LifeEventFrequency,
  LifeEventDirection,
  LifeEventKind,
  EXCLUDED_PENSION_TYPES,
} from "./constants";
import type { ProductBreakdown, LifeEvent } from "./types";
import { convertToBaseCurrency, applyOwnership, generateId } from "./utils";

// ============================================================================
// PENSION UTILITIES
// ============================================================================

/**
 * Check if a pension product should be excluded from retirement pension calculation
 * (e.g., קרנות השתלמות which is a savings fund, not a retirement pension)
 */
export function isExcludedPensionType(pensionProduct: PensionAsset): boolean {
  const productType = pensionProduct.productType?.value;
  if (!productType) return false;
  return EXCLUDED_PENSION_TYPES.includes(productType);
}

// ============================================================================
// PRODUCT MAPPING FUNCTIONS
// ============================================================================

/**
 * Map a bank product to portfolio category
 */
export function mapBankProduct(
  product: Record<string, any>,
  fiatData: Fiat[]
): ProductBreakdown {
  const bank = product as BankAsset & { id: number | string };
  const valueInOriginal = bank.amount || 0;
  const valueInBase = convertToBaseCurrency(valueInOriginal, bank.currency, fiatData);
  const valueWithOwnership = applyOwnership(valueInBase, bank.ownership);

  // Savings/deposit accounts earn interest (DEPOSIT), checking accounts don't (CASH)
  const isSavingsAccount = bank.accountType?.value === "saving";

  return {
    productId: product.id,
    productName: bank.name,
    form: Forms.BANK,
    category: isSavingsAccount ? AssetCategory.DEPOSIT : AssetCategory.CASH,
    originalValue: valueInOriginal,
    originalCurrency: bank.currency?.value || "ILS",
    valueInBaseCurrency: valueWithOwnership,
    ownership: bank.ownership ?? 100,
    isLiquid: bank.liquidity !== false,
    includedInPortfolio: true,
  };
}

/**
 * Map a share product to portfolio category
 */
export function mapShareProduct(
  product: Record<string, any>,
  fiatData: Fiat[]
): ProductBreakdown {
  const share = product as ShareAsset & { id: number | string };
  let totalValue = 0;

  // Calculate total value from stocks data
  if (share.stocksData && Array.isArray(share.stocksData)) {
    for (const stock of share.stocksData) {
      const stockValue = (stock.stockPriceInBaseCurrency || stock.stockPrice || 0) * (stock.amount || 0);
      // stockPriceInBaseCurrency is already in ILS
      if (stock.stockPriceInBaseCurrency) {
        totalValue += stockValue;
      } else {
        totalValue += convertToBaseCurrency(stockValue, stock.currency, fiatData);
      }
    }
  }

  // Add cash if any
  if (share.cash) {
    totalValue += convertToBaseCurrency(share.cash, share.currency, fiatData);
  }

  const valueWithOwnership = applyOwnership(totalValue, share.ownership);

  return {
    productId: product.id,
    productName: share.name,
    form: Forms.SHARE,
    category: AssetCategory.STOCKS,
    originalValue: totalValue,
    originalCurrency: "ILS", // Already converted
    valueInBaseCurrency: valueWithOwnership,
    ownership: share.ownership ?? 100,
    isLiquid: share.liquidity !== false,
    includedInPortfolio: true,
  };
}

/**
 * Map an RSU product to portfolio category
 * Returns both vested and unvested breakdowns
 */
export function mapRSUProduct(
  product: Record<string, any>,
  fiatData: Fiat[],
  includeUnvested: boolean
): { vested: ProductBreakdown; unvestedEvents: LifeEvent[] } {
  const rsu = product as RSUAsset & { id: number | string };
  let vestedValue = 0;
  const unvestedEvents: LifeEvent[] = [];
  const stockPrice = rsu.stockPriceInBaseCurrency || rsu.stockPrice || 0;

  // Calculate vested value and create events for unvested
  if (rsu.rsuData && Array.isArray(rsu.rsuData)) {
    for (const grant of rsu.rsuData) {
      const vestedAmount = grant.vestedAmount || 0;
      const totalAmount = grant.totalAmount || 0;
      const unvestedAmount = totalAmount - vestedAmount;

      // Vested portion
      const grantVestedValue = vestedAmount * stockPrice;
      vestedValue += grantVestedValue;

      // Create life events for unvested portions (best effort vesting schedule)
      if (unvestedAmount > 0 && grant.grantDate) {
        const grantDate = new Date(grant.grantDate);
        const currentYear = new Date().getFullYear();
        // Assume 4-year vesting with annual cliff (common)
        const yearsRemaining = Math.max(0, 4 - (currentYear - grantDate.getFullYear()));
        
        if (yearsRemaining > 0 && includeUnvested) {
          const annualVestAmount = unvestedAmount / yearsRemaining;
          for (let i = 1; i <= yearsRemaining; i++) {
            unvestedEvents.push({
              kind: LifeEventKind.CASHFLOW,
              id: generateId(),
              name: `RSU Vesting: ${rsu.name}`,
              startYear: currentYear + i,
              frequency: LifeEventFrequency.ONE_TIME,
              amount: annualVestAmount * stockPrice,
              direction: LifeEventDirection.INFLOW,
              categoryTag: "RSU Vesting",
              enabled: true,
              notes: `Estimated vesting from ${rsu.name}`,
            });
          }
        }
      }
    }
  }

  const valueWithOwnership = applyOwnership(vestedValue, rsu.ownership);

  return {
    vested: {
      productId: product.id,
      productName: rsu.name,
      form: Forms.RSU,
      category: AssetCategory.RSU,
      originalValue: vestedValue,
      originalCurrency: "ILS",
      valueInBaseCurrency: valueWithOwnership,
      ownership: rsu.ownership ?? 100,
      isLiquid: rsu.liquidity !== false,
      includedInPortfolio: true,
      notes: rsu.showVestedOnly ? "Vested only" : "Includes unvested",
    },
    unvestedEvents,
  };
}

/**
 * Map a crypto product to portfolio category
 */
export function mapCryptoProduct(
  product: Record<string, any>,
  fiatData: Fiat[],
  cryptoMarketData?: { name: string; value: string; rate: number }[]
): ProductBreakdown {
  const crypto = product as CryptoAsset & { id: number | string };
  let totalValue = 0;

  // Calculate total value from crypto data
  if (crypto.cryptoData && Array.isArray(crypto.cryptoData)) {
    for (const coin of crypto.cryptoData) {
      // Use current market rate if available, otherwise fall back to stored rate
      let coinRate = coin.coinRate || 0;
      if (cryptoMarketData && coin.coinName) {
        const marketCoin = cryptoMarketData.find(
          (c) => c.value === coin.coinName
        );
        if (marketCoin && marketCoin.rate > 0) {
          coinRate = marketCoin.rate;
        }
      }
      const coinValue = coinRate * (coin.amount || 0);
      // coinRate is typically in USD, convert to ILS
      totalValue += convertToBaseCurrency(coinValue, "USD", fiatData);
    }
  }

  // Add cash if any
  if (crypto.cash) {
    totalValue += convertToBaseCurrency(crypto.cash, crypto.currency, fiatData);
  }

  const valueWithOwnership = applyOwnership(totalValue, crypto.ownership);

  return {
    productId: product.id,
    productName: crypto.name,
    form: Forms.CRYPTO,
    category: AssetCategory.CRYPTO,
    originalValue: totalValue,
    originalCurrency: "ILS",
    valueInBaseCurrency: valueWithOwnership,
    ownership: crypto.ownership ?? 100,
    isLiquid: crypto.liquidity !== false,
    includedInPortfolio: true,
  };
}

/**
 * Map a pension product to portfolio category
 * Note: קרן השתלמות (Keren Hishtalmut) is excluded from portfolio calculations
 * as it's a savings fund, not a retirement pension
 */
export function mapPensionProduct(
  product: Record<string, any>,
  fiatData: Fiat[]
): ProductBreakdown {
  const pension = product as PensionAsset & { id: number | string };
  const valueInOriginal = pension.amount || 0;
  const valueInBase = convertToBaseCurrency(valueInOriginal, pension.currency, fiatData);
  // Pension typically doesn't have ownership split
  const ownership = 100;
  
  // Check if this is קרן השתלמות (Keren Hishtalmut)
  const isKerenHistalmut = isExcludedPensionType(pension);

  return {
    productId: product.id,
    productName: pension.name,
    form: Forms.PENSION,
    category: AssetCategory.PENSION,
    originalValue: valueInOriginal,
    originalCurrency: pension.currency?.value || "ILS",
    valueInBaseCurrency: valueInBase,
    ownership,
    isLiquid: pension.liquidity === true, // Most pensions are non-liquid
    // Include קרן השתלמות by default (it's a savings fund that can be used for retirement)
    includedInPortfolio: true,
    notes: isKerenHistalmut ? "קרן השתלמות" : undefined,
  };
}

/**
 * Map a real estate product to portfolio category
 */
export function mapRealestateProduct(
  product: Record<string, any>,
  fiatData: Fiat[]
): ProductBreakdown {
  const realestate = product as RealestateAsset & { id: number | string };
  const valueInOriginal = realestate.value || 0;
  const valueInBase = convertToBaseCurrency(valueInOriginal, realestate.currency, fiatData);
  const valueWithOwnership = applyOwnership(valueInBase, realestate.ownership);

  return {
    productId: product.id,
    productName: realestate.name,
    form: Forms.REALESTATE,
    category: AssetCategory.REALESTATE,
    originalValue: valueInOriginal,
    originalCurrency: realestate.currency?.value || "ILS",
    valueInBaseCurrency: valueWithOwnership,
    ownership: realestate.ownership ?? 100,
    isLiquid: realestate.liquidity === true, // Real estate is typically non-liquid
    includedInPortfolio: true,
  };
}

/**
 * Map an "other" product to portfolio category
 */
export function mapOtherProduct(
  product: Record<string, any>,
  fiatData: Fiat[]
): ProductBreakdown {
  const other = product as OtherAsset & { id: number | string };
  const valueInOriginal = other.currentValue || 0;
  const valueInBase = convertToBaseCurrency(valueInOriginal, other.currency, fiatData);
  const valueWithOwnership = applyOwnership(valueInBase, other.ownership);

  return {
    productId: product.id,
    productName: other.name,
    form: Forms.OTHER,
    category: AssetCategory.OTHER,
    originalValue: valueInOriginal,
    originalCurrency: other.currency?.value || "ILS",
    valueInBaseCurrency: valueWithOwnership,
    ownership: other.ownership ?? 100,
    isLiquid: other.liquidity !== false,
    includedInPortfolio: true,
  };
}

/**
 * Map a debt product and extract payment information
 */
export function mapDebtProduct(
  product: Record<string, any>,
  fiatData: Fiat[]
): { remainder: number; monthlyPayment: number; productId: number | string; productName: string; debtType?: string } {
  const debt = product as DebtAsset & { id: number | string };
  let totalRemainder = 0;
  let totalMonthlyPayment = 0;

  if (debt.routesData && Array.isArray(debt.routesData)) {
    for (const route of debt.routesData) {
      totalRemainder += route.remainder || 0;
      
      // Use monthly repayment if available, otherwise estimate
      if (route.monthlyRepayment) {
        totalMonthlyPayment += route.monthlyRepayment;
      } else if (route.remainder && route.debtPeriodInMonths && route.debtPeriodInMonths > 1) {
        // Simple estimation: remainder / remaining months (ignoring interest for simplicity)
        totalMonthlyPayment += route.remainder / route.debtPeriodInMonths;
      }
    }
  }

  // Convert to base currency
  const remainderInBase = convertToBaseCurrency(totalRemainder, debt.currency, fiatData);
  const monthlyInBase = convertToBaseCurrency(totalMonthlyPayment, debt.currency, fiatData);
  
  // Apply ownership
  const ownershipRatio = (debt.ownership ?? 100) / 100;

  return {
    remainder: remainderInBase * ownershipRatio,
    monthlyPayment: monthlyInBase * ownershipRatio,
    productId: product.id,
    productName: debt.name,
    debtType: debt.debtType,
  };
}
